<script lang="ts">
  import { navSection, type Route } from '../lib/router';

  /** ヘッダ（layout-and-navigation.md §2）。主ナビは 問題 / レッスン / ラボ / メーター と設定 */
  interface Props {
    route: Route;
    /** セッション中（URL 直打ちのときだけ）はナビを畳み、中断と何枚目かに置き換える */
    session?: { index: number; total: number } | undefined;
    onabort?: (() => void) | undefined;
  }

  let { route, session, onabort }: Props = $props();

  const current = $derived(navSection(route));
  const items = [
    { id: 'problems', href: '#/', label: '問題' },
    { id: 'lessons', href: '#/lessons', label: 'レッスン' },
    { id: 'lab', href: '#/lab', label: 'ラボ' },
    { id: 'meter', href: '#/meter', label: 'メーター' },
  ] as const;
  const pct = $derived(
    session
      ? Math.round((Math.min(session.index, session.total) / Math.max(session.total, 1)) * 100)
      : 0,
  );
</script>

<header class="scoreboard" data-testid="scoreboard">
  {#if session}
    <button class="board-btn" type="button" onclick={() => onabort?.()} data-testid="abort">
      中断 <kbd>Esc</kbd>
    </button>
    <div
      class="session-progress"
      role="progressbar"
      aria-label="セッションの進み具合"
      aria-valuemin={0}
      aria-valuemax={session.total}
      aria-valuenow={session.index}
      aria-valuetext="{session.total} 枚中 {session.index} 枚目"
    >
      <span class="num">{session.index} / {session.total}</span>
      <span class="bar"><span style:width="{pct}%"></span></span>
    </div>
    <span class="spacer"></span>
  {:else}
    <a class="brand" href="#/" aria-label="inductor-class ホーム">
      <span class="logo-mark" aria-hidden="true">i</span><span class="brand-name"
        >inductor-class</span
      >
    </a>
    <nav class="mainnav" aria-label="メイン">
      {#each items as item (item.id)}
        <a href={item.href} aria-current={current === item.id ? 'page' : undefined}>{item.label}</a>
      {/each}
    </nav>
    <span class="spacer"></span>
  {/if}

  {#if !session}
    <a
      class="settings-link"
      href="#/settings"
      aria-label="設定"
      aria-current={current === 'settings' ? 'page' : undefined}
      data-testid="nav-settings"
    >
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="2.6" fill="none" stroke="currentColor" stroke-width="1.6" />
        <path
          d="M10 2.5v2.3M10 15.2v2.3M2.5 10h2.3M15.2 10h2.3M4.7 4.7l1.6 1.6M13.7 13.7l1.6 1.6M4.7 15.3l1.6-1.6M13.7 6.3l1.6-1.6"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>
    </a>
  {/if}
</header>

<style>
  .scoreboard {
    position: sticky;
    top: 0;
    z-index: var(--z-scoreboard);
    height: var(--scoreboard-h);
    display: flex;
    align-items: center;
    gap: var(--space-4);
    padding: 0 var(--space-4);
    background: var(--scoreboard);
    border-bottom: 1px solid var(--scoreboard-border);
    color: var(--text-on-scoreboard);
  }
  .scoreboard a {
    color: inherit;
  }
  .scoreboard a:hover {
    text-decoration: none;
  }
  .brand {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-weight: var(--fw-bold);
    font-size: var(--fs-md);
    white-space: nowrap;
    min-height: var(--tap-min);
  }
  .logo-mark {
    display: inline-grid;
    place-items: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 34%;
    background: linear-gradient(135deg, var(--brand-a), var(--brand-b));
    color: var(--text-on-scoreboard);
    font-weight: var(--fw-heavy);
    font-size: 0.95rem;
  }
  .mainnav {
    display: flex;
    gap: var(--space-1);
    min-width: 0;
  }
  .mainnav a {
    display: inline-flex;
    align-items: center;
    min-height: 2rem;
    padding: var(--space-1) var(--space-3);
    border-radius: var(--radius-pill);
    font-size: var(--fs-sm);
    font-weight: var(--fw-semibold);
    color: var(--text-on-scoreboard-soft);
    white-space: nowrap;
  }
  .mainnav a:hover {
    color: var(--text-on-scoreboard);
  }
  .mainnav a[aria-current='page'] {
    color: var(--text-on-scoreboard);
    background: color-mix(in srgb, var(--text-on-scoreboard) 12%, transparent);
  }
  .spacer {
    flex: 1;
  }
  .settings-link {
    display: inline-grid;
    place-items: center;
    flex: none;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: var(--radius-pill);
    color: var(--text-on-scoreboard-soft) !important;
  }
  .settings-link:hover,
  .settings-link[aria-current='page'] {
    color: var(--text-on-scoreboard) !important;
    background: color-mix(in srgb, var(--text-on-scoreboard) 12%, transparent);
  }
  .settings-link svg {
    width: 1.25rem;
    height: 1.25rem;
  }
  .session-progress {
    flex: 1;
    min-width: 6rem;
    max-width: 22rem;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: var(--space-3);
    font-size: var(--fs-sm);
    color: var(--text-on-scoreboard-soft);
  }
  .session-progress .bar {
    flex: 1;
    min-width: 2.5rem;
    height: 6px;
    border-radius: var(--radius-pill);
    background: color-mix(in srgb, var(--text-on-scoreboard) 22%, transparent);
    overflow: hidden;
  }
  .session-progress .bar span {
    display: block;
    height: 100%;
    background: var(--accent-on-scoreboard);
    border-radius: inherit;
    transition: width var(--dur-base) var(--ease-out);
  }
  .board-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    min-height: 2rem;
    padding: 0 var(--space-3);
    border-radius: var(--radius-sm);
    border: 1px solid var(--text-on-scoreboard-soft);
    background: transparent;
    color: var(--text-on-scoreboard);
    font-size: var(--fs-sm);
    font-weight: var(--fw-semibold);
    white-space: nowrap;
  }
  .board-btn kbd {
    background: transparent;
    color: inherit;
    border-color: var(--text-on-scoreboard-soft);
  }
  .scoreboard :focus-visible {
    outline-color: var(--focus);
  }
  @media (max-width: 639px) {
    .scoreboard {
      gap: var(--space-2);
      padding: 0 var(--space-3);
    }
    .brand-name {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      white-space: nowrap;
    }
    .mainnav a {
      padding: var(--space-1) var(--space-2);
    }
  }
  @media (max-width: 479px) {
    .mainnav {
      gap: 0;
    }
  }
  @media (max-width: 359px) {
    .mainnav a {
      padding: var(--space-1) var(--space-1);
      font-size: var(--fs-xs);
    }
  }
</style>
